// Client-side image quality heuristics so we can refuse a clearly
// unreadable photo before burning Cloud Vision quota on it. Two
// signals, both run on a small downscaled thumbnail of the canvas
// so they cost <10ms:
//
//   - Blur: variance of the Laplacian (high variance = sharp edges,
//     low variance = blurry). Threshold tuned against ~50 senior
//     test shots: variance < 100 is "too blurry to bother."
//
//   - Glare: fraction of pixels with luminance > 240 (near-white).
//     Above ~15% means glare dominates and OCR will miss letters
//     under the highlights.
//
// Both are heuristics, not perfect, but they catch the obvious
// failure modes (hand shake, flash bouncing off a glossy label).

export interface QualityReport {
  variance: number;
  glareFraction: number;
  ok: boolean;
  reason: 'ok' | 'blurry' | 'glare' | 'too-dark';
  message: string | null;
}

const BLUR_THRESHOLD = 90;
const GLARE_THRESHOLD = 0.18;
const DARK_THRESHOLD = 32;

export async function assessImageQuality(blob: Blob): Promise<QualityReport> {
  const bitmap = await createImageBitmap(blob);
  // Downscale to a fixed 240px wide thumbnail so the variance is
  // comparable across phone resolutions and we don't burn CPU on
  // a 4032x3024 buffer.
  const targetW = 240;
  const scale = targetW / bitmap.width;
  const w = targetW;
  const h = Math.round(bitmap.height * scale);
  const canvas = document.createElement('canvas');
  canvas.width = w;
  canvas.height = h;
  const ctx = canvas.getContext('2d', { willReadFrequently: true });
  if (!ctx) {
    return {
      variance: 0,
      glareFraction: 0,
      ok: true,
      reason: 'ok',
      message: null,
    };
  }
  ctx.drawImage(bitmap, 0, 0, w, h);
  const { data } = ctx.getImageData(0, 0, w, h);

  // Build a luminance buffer (one byte per pixel) for the Laplacian.
  const lum = new Float32Array(w * h);
  let glareCount = 0;
  let meanLum = 0;
  for (let i = 0, p = 0; i < data.length; i += 4, p++) {
    const r = data[i] ?? 0;
    const g = data[i + 1] ?? 0;
    const b = data[i + 2] ?? 0;
    const y = 0.299 * r + 0.587 * g + 0.114 * b;
    lum[p] = y;
    meanLum += y;
    if (y > 240) glareCount++;
  }
  meanLum /= w * h;
  const glareFraction = glareCount / (w * h);

  // Discrete Laplacian: L(x,y) = -4*c + n + s + e + w. Variance of L
  // across the inner pixels is the blur metric.
  let sum = 0;
  let sumSq = 0;
  let n = 0;
  for (let y = 1; y < h - 1; y++) {
    for (let x = 1; x < w - 1; x++) {
      const c = lum[y * w + x] ?? 0;
      const up = lum[(y - 1) * w + x] ?? 0;
      const dn = lum[(y + 1) * w + x] ?? 0;
      const lf = lum[y * w + (x - 1)] ?? 0;
      const rt = lum[y * w + (x + 1)] ?? 0;
      const lap = up + dn + lf + rt - 4 * c;
      sum += lap;
      sumSq += lap * lap;
      n++;
    }
  }
  const mean = sum / n;
  const variance = sumSq / n - mean * mean;

  if (meanLum < DARK_THRESHOLD) {
    return {
      variance,
      glareFraction,
      ok: false,
      reason: 'too-dark',
      message: 'It looks too dark. Try moving to better light, then retake.',
    };
  }
  if (variance < BLUR_THRESHOLD) {
    return {
      variance,
      glareFraction,
      ok: false,
      reason: 'blurry',
      message: 'The photo is a little blurry. Hold the camera steady and retake.',
    };
  }
  if (glareFraction > GLARE_THRESHOLD) {
    return {
      variance,
      glareFraction,
      ok: false,
      reason: 'glare',
      message:
        'There is a lot of glare on the label. Tilt the bottle slightly away from the light and retake.',
    };
  }
  return { variance, glareFraction, ok: true, reason: 'ok', message: null };
}
